import type { UseCaseEnvironment } from "@dashboardApi/apis/offerfitShared/types";
import type { BaseTableParams } from "@dashboardApi/apis/types";
import type { BaseTableResultItem, TableResult } from "@tanstack/react-table";

import type { FetchRequestError } from "@lib/errors";
import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import type { SHARED_CONSTANTS_DECISIONING_STUDIO_ERROR_CODES } from "@lib/shared_constants";

export type { UseCaseEnvironment };

export interface DecisioningStudioDimensionActionField {
  name: string;
  value: string | number | boolean | null;
}

export interface DecisioningStudioDimensionActionResultItem extends BaseTableResultItem {
  id: string;
  key: string;
  fields: DecisioningStudioDimensionActionField[];
  created_at: string;
  updated_at: string;
}

export interface DecisioningStudioAgentsAppGroupIdDsaIdDimensionsDimensionActionsQuery {
  /** optional search term to filter actions by name */
  search_term?: string;
  /** optional status filter (e.g. "active") */
  status?: string;
}

export interface DecisioningStudioAgentsAppGroupIdDsaIdDimensionsDimensionActionsParams extends BaseTableParams {
  groupby?: never;
  sortby?: never;
  sortdir?: never;
  /** the unique identifier for the decisioning studio agent */
  dsaId: string;
  /** the dimension slug (e.g. "email", "sms") */
  dimension: string;
  /** optional filter parameters */
  query?: DecisioningStudioAgentsAppGroupIdDsaIdDimensionsDimensionActionsQuery;
  /** Which environment to read from. Defaults to "draft". */
  environment?: UseCaseEnvironment;
}

export type GetDataTableDecisioningStudioAgentsAppGroupIdDsaIdDimensionsDimensionActionsResult =
  TableResult<DecisioningStudioDimensionActionResultItem>;

export interface DimensionActionsApiResponse {
  data: DecisioningStudioDimensionActionResultItem[];
  meta: {
    pagination: {
      total: number;
      offset: number;
      limit: number;
    };
    extra: null;
  };
}

export interface PostDecisioningStudioAgentsAppGroupIdDsaIdDimensionsDimensionActionsProps {
  appGroupId: string;
  dsaId: string;
  dimension: string;
  fields: DecisioningStudioDimensionActionField[];
}

export interface PostDecisioningStudioAgentsAppGroupIdDsaIdDimensionsDimensionActionsResult {
  data: {
    action_id: number;
    action_status: string;
    created_date: string;
    /** the submitted fields echoed back, excluding the system-managed columns */
    fields: DecisioningStudioDimensionActionField[];
  };
  meta: null;
}

export interface DecisioningStudioDimensionActionUpsertRow {
  /**
   * The row names the action it updates through an `action_id` entry in `fields`; a row without
   * one creates an action. Conman models a row as its field list alone and ignores any key sent
   * beside it, so an action_id placed next to `fields` is dropped and the row creates a duplicate
   * rather than updating.
   */
  fields: DecisioningStudioDimensionActionField[];
}

export interface PatchDecisioningStudioAgentsAppGroupIdDsaIdDimensionsDimensionActionsProps {
  appGroupId: string;
  dsaId: string;
  dimension: string;
  actions: DecisioningStudioDimensionActionUpsertRow[];
}

export interface PatchDecisioningStudioAgentsAppGroupIdDsaIdDimensionsDimensionActionsResult {
  data: DecisioningStudioDimensionActionResultItem[];
  meta: {
    pagination: {
      total: number;
      /** null on a write: the upsert answers with only the rows it wrote, so it pages nothing. */
      offset: number | null;
      /** null on a write, for the same reason as offset. */
      limit: number | null;
    };
    extra: null;
  };
}

/**
 * Every code the three action bank write endpoints answer a failure with: the five Conman statuses
 * the controller maps, its catch-all for anything else, the platform's own payload check, and the
 * gates a write passes before it reaches Conman — the Pro tier check, the agent lookup, and the
 * Decisioning Studio access check. The flipper half of the write gate is deliberately absent: it
 * answers with a body carrying no code at all.
 */
export type DecisioningStudioActionBankWriteErrorCode =
  | SHARED_CONSTANTS_DECISIONING_STUDIO_ERROR_CODES["ACTION_BANK_WRITE_INVALID_REQUEST"]
  | SHARED_CONSTANTS_DECISIONING_STUDIO_ERROR_CODES["ACTION_BANK_WRITE_FORBIDDEN"]
  | SHARED_CONSTANTS_DECISIONING_STUDIO_ERROR_CODES["ACTION_BANK_WRITE_NOT_FOUND"]
  | SHARED_CONSTANTS_DECISIONING_STUDIO_ERROR_CODES["ACTION_BANK_WRITE_CONFLICT"]
  | SHARED_CONSTANTS_DECISIONING_STUDIO_ERROR_CODES["ACTION_BANK_VALIDATION_FAILED"]
  | SHARED_CONSTANTS_DECISIONING_STUDIO_ERROR_CODES["FAILED_TO_WRITE_DIMENSION_ACTIONS"]
  | SHARED_CONSTANTS_DECISIONING_STUDIO_ERROR_CODES["MISSING_REQUIRED_PARAMETERS"]
  | SHARED_CONSTANTS_DECISIONING_STUDIO_ERROR_CODES["ACTION_BANK_WRITE_REQUIRES_PRO_AGENT"]
  | SHARED_CONSTANTS_DECISIONING_STUDIO_ERROR_CODES["DECISIONING_STUDIO_AGENT_NOT_FOUND"]
  | SHARED_CONSTANTS_DECISIONING_STUDIO_ERROR_CODES["DECISIONING_STUDIO_NOT_ENABLED"];

/**
 * A failed action bank write that arrived as the coded envelope. None of these codes carries a
 * payload of its own, so one type covers the create, the update and the bulk upsert alike.
 */
export type DecisioningStudioActionBankWriteError =
  FetchUserEncodedError<DecisioningStudioActionBankWriteErrorCode>;

/**
 * Narrows a failed write to the coded envelope, so callers compare `errorCode` against the code
 * union rather than against a bare string. A body that is not the envelope — the flipper gate's
 * plain JSON, a gateway's HTML, a transport failure — stays a FetchRequestError and is left to be
 * classified by status.
 */
export const isDecisioningStudioActionBankWriteError = (
  error: FetchRequestError
): error is DecisioningStudioActionBankWriteError => {
  return error instanceof FetchUserEncodedError;
};
